import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";
import { clientesApi } from "@/lib/clientesApi";
import { toast } from "sonner";

const MENSAGENS = {
  custom_field_name_required: "Dê um nome a todos os campos antes de salvar.",
  custom_field_duplicated: "Há dois campos com o mesmo nome. Use nomes diferentes.",
  client_operationally_blocked: "Este cliente está arquivado ou bloqueado e não pode ser editado.",
};

// Cada escritório controla informação que o cadastro fixo não prevê — número
// de contrato, indicação, processo administrativo. Nome e valor livres cobrem
// isso sem exigir alteração de esquema a cada pedido.
export default function CamposCustomizados({ client, onSaved }) {
  const [campos, setCampos] = useState(() => client?.campos_customizados || []);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const alterar = (indice, chave) => (event) => {
    const copia = [...campos];
    copia[indice] = { ...copia[indice], [chave]: event.target.value };
    setCampos(copia);
  };
  const adicionar = () => setCampos([...campos, { nome: "", valor: "" }]);
  const remover = (indice) => setCampos(campos.filter((_, i) => i !== indice));

  const salvar = async () => {
    setErro("");
    setSalvando(true);
    try {
      await clientesApi.update(client.cliente_id, {
        expected_version: client.version,
        campos_customizados: campos.map((c) => ({ nome: c.nome.trim(), valor: (c.valor || "").trim() })),
      });
      toast.success("Campos salvos");
      onSaved?.();
    } catch (err) {
      const detalhe = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        setErro("Os dados mudaram em outra tela. Recarregue e tente de novo.");
        onSaved?.();
      } else {
        setErro(MENSAGENS[detalhe?.code] || "Não foi possível salvar os campos.");
      }
    } finally {
      setSalvando(false);
    }
  };

  if (!client) return null;

  return (
    <section className="bg-white border border-[#D1DEEE] rounded-lg p-6" data-testid="campos-customizados">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-[#001533]">Campos personalizados</h2>
          <p className="text-[#335480] text-sm mt-0.5">Informações próprias do seu escritório sobre este cliente.</p>
        </div>
        <Button type="button" variant="outline" onClick={adicionar} data-testid="add-campo">
          <Plus size={16} /> Adicionar campo
        </Button>
      </div>

      {campos.length === 0 ? (
        <p className="text-[#335480]">Nenhum campo personalizado. Use “Adicionar campo” para criar o primeiro.</p>
      ) : (
        <div className="space-y-2">
          {campos.map((campo, indice) => (
            <div key={indice} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
              <Input
                data-testid={`campo-nome-${indice}`}
                value={campo.nome}
                onChange={alterar(indice, "nome")}
                placeholder="Nome do campo (ex.: Contrato)"
                maxLength={60}
              />
              <Input
                data-testid={`campo-valor-${indice}`}
                value={campo.valor || ""}
                onChange={alterar(indice, "valor")}
                placeholder="Valor"
                maxLength={500}
              />
              <Button type="button" variant="outline" onClick={() => remover(indice)} data-testid={`remover-campo-${indice}`}>
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {erro && <p role="alert" className="text-red-700 text-sm mt-3">{erro}</p>}
      <Button type="button" onClick={salvar} disabled={salvando} data-testid="salvar-campos" className="mt-4">
        {salvando ? "Salvando..." : "Salvar campos"}
      </Button>
    </section>
  );
}
